import { useCallback, useMemo, useState } from 'react';
import type { FormInstance } from 'antd/es/form';
import type { IntlShape } from 'react-intl';

import { CertificatePaymentModeEnum } from '@share/common-types';

import type { PaymentFormValues } from '../types';

import {
  createDownPaymentRules,
  createFullSplitFieldRules,
  createInstalmentDownSplitFieldRules,
} from '../validation';
import {
  buildInstalmentPaymentSchedule,
  buildInstalmentSummary,
  INSTALMENT_FIXED_FEE_PER_PAYMENT_USD,
  minDownPaymentAmount,
  parseMoneyInput,
  roundCurrency2,
  type InstalmentSummary,
  type PaymentScheduleEntry,
} from '../utils/payment-math';
import { resolveCertificatePaymentMode } from '../utils/resolve-payment-mode';

import type { PaymentFormWatchedValues } from './usePaymentFormWatchedValues';

interface UsePaymentFormDerivedParams {
  intl: IntlShape;
  form: FormInstance<PaymentFormValues>;
  certificateTotal: number;
  watched: PaymentFormWatchedValues;
}

export interface PaymentFormDerived {
  minDown: number;
  instalmentSummary: InstalmentSummary | null;
  scheduleEntries: PaymentScheduleEntry[];
  scheduleTotal: number;
  downPaymentRules: ReturnType<typeof createDownPaymentRules>;
  splitFullRules1: ReturnType<typeof createFullSplitFieldRules>;
  splitFullRules2: ReturnType<typeof createFullSplitFieldRules>;
  splitDownRules1: ReturnType<typeof createInstalmentDownSplitFieldRules>;
  splitDownRules2: ReturnType<typeof createInstalmentDownSplitFieldRules>;
  downForSplitRules: number;
  showInstalmentBlock: boolean;
  showSplitPayInFull: boolean;
  showSplitInstalmentDown: boolean;
  showSecondCard: boolean;
  hidePaymentDetailsForInstalments: boolean;
  agreementChargeLabel: string;
  cardOneSectionLabel: string;
  scheduleModalOpen: boolean;
  handleOpenPaymentSchedule: () => void;
  handleClosePaymentSchedule: () => void;
}

export function usePaymentFormDerived({
  intl,
  form,
  certificateTotal,
  watched,
}: UsePaymentFormDerivedParams): PaymentFormDerived {
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);

  const minDown = useMemo(() => minDownPaymentAmount(certificateTotal), [certificateTotal]);

  const { paymentPlan, splitPayInFull, splitInstalmentDown, instalmentMonths, downPaymentAmount } =
    watched;

  const resolvedPaymentMode = useMemo(
    () =>
      resolveCertificatePaymentMode({
        paymentPlan: paymentPlan ?? 'full',
        splitPayInFull: splitPayInFull ?? false,
        splitInstalmentDown: splitInstalmentDown ?? false,
      }),
    [paymentPlan, splitPayInFull, splitInstalmentDown],
  );

  const instalmentSummary = useMemo(() => {
    if (paymentPlan !== 'instalments') {
      return null;
    }
    const months = instalmentMonths ?? 4;
    const parsed = parseMoneyInput(String(downPaymentAmount ?? ''));
    const numericDown = parsed !== null ? parsed : minDown;
    const clampedDown = roundCurrency2(Math.min(Math.max(numericDown, minDown), certificateTotal));
    return buildInstalmentSummary({
      certificateTotal,
      instalmentMonths: months,
      downPayment: clampedDown,
    });
  }, [paymentPlan, instalmentMonths, downPaymentAmount, certificateTotal, minDown]);

  const { scheduleEntries, scheduleTotal } = useMemo(() => {
    if (!instalmentSummary) {
      return { scheduleEntries: [] as PaymentScheduleEntry[], scheduleTotal: 0 };
    }
    const built = buildInstalmentPaymentSchedule({
      certificateTotal: instalmentSummary.certificateTotal,
      downPayment: instalmentSummary.downPayment,
      instalmentMonths: instalmentSummary.instalmentMonths,
    });
    const totalMonths = instalmentSummary.instalmentMonths;
    const entries = built.entries.map((e) => ({
      ...e,
      paymentDescription: e.isDeposit
        ? intl.formatMessage({ id: 'certificates.payment.scheduleRow.deposit' }, { totalMonths })
        : intl.formatMessage(
            { id: 'certificates.payment.scheduleRow.instalment' },
            { row: e.rowNumber, totalMonths },
          ),
    }));
    return { scheduleEntries: entries, scheduleTotal: built.total };
  }, [instalmentSummary, intl]);

  const downPaymentRules = useMemo(
    () => createDownPaymentRules({ intl, form, certificateTotal }),
    [intl, form, certificateTotal],
  );

  const splitFullRules1 = useMemo(
    () => createFullSplitFieldRules({ intl, form, certificateTotal, peerField: 'splitAmount2' }),
    [intl, form, certificateTotal],
  );
  const splitFullRules2 = useMemo(
    () => createFullSplitFieldRules({ intl, form, certificateTotal, peerField: 'splitAmount1' }),
    [intl, form, certificateTotal],
  );

  const downParsed = parseMoneyInput(String(downPaymentAmount ?? ''));
  const downForSplitRules = roundCurrency2(downParsed !== null ? downParsed : minDown);

  const splitDownRules1 = useMemo(
    () =>
      createInstalmentDownSplitFieldRules({
        intl,
        form,
        downPaymentAmount: downForSplitRules,
        peerField: 'splitDownAmount2',
      }),
    [intl, form, downForSplitRules],
  );
  const splitDownRules2 = useMemo(
    () =>
      createInstalmentDownSplitFieldRules({
        intl,
        form,
        downPaymentAmount: downForSplitRules,
        peerField: 'splitDownAmount1',
      }),
    [intl, form, downForSplitRules],
  );

  const handleOpenPaymentSchedule = useCallback(() => {
    setScheduleModalOpen(true);
  }, []);

  const handleClosePaymentSchedule = useCallback(() => {
    setScheduleModalOpen(false);
  }, []);

  const showInstalmentBlock = paymentPlan === 'instalments';
  const showSplitPayInFull = paymentPlan === 'full' && splitPayInFull;
  const showSplitInstalmentDown = paymentPlan === 'instalments' && splitInstalmentDown;
  const showSecondCard =
    resolvedPaymentMode === CertificatePaymentModeEnum.PayInFullSplit ||
    resolvedPaymentMode === CertificatePaymentModeEnum.InstalmentsSplitDownPayment;

  const hidePaymentDetailsForInstalments = showInstalmentBlock;

  const agreementChargeLabel =
    showInstalmentBlock && instalmentSummary
      ? (() => {
          const fee = INSTALMENT_FIXED_FEE_PER_PAYMENT_USD;
          const feeSentence =
            fee > 0
              ? intl.formatMessage(
                  { id: 'certificates.payment.agreement.instalmentFee' },
                  { fee: `$${fee.toFixed(2)}` },
                )
              : '';
          return intl.formatMessage(
            { id: 'certificates.payment.agreement.chargeInstalment' },
            {
              downPayment: instalmentSummary.downPayment.toFixed(2),
              months: instalmentSummary.instalmentMonths,
              feeSentence,
            },
          );
        })()
      : intl.formatMessage(
          { id: 'certificates.payment.agreement.chargeFull' },
          { price: (certificateTotal ?? 0).toFixed(2) },
        );

  const cardOneSectionLabel = showSecondCard
    ? intl.formatMessage({ id: 'certificates.payment.firstPaymentSection' })
    : intl.formatMessage({ id: 'certificates.payment.cardLabel' });

  return {
    minDown,
    instalmentSummary,
    scheduleEntries,
    scheduleTotal,
    downPaymentRules,
    splitFullRules1,
    splitFullRules2,
    splitDownRules1,
    splitDownRules2,
    downForSplitRules,
    showInstalmentBlock,
    showSplitPayInFull: showSplitPayInFull ?? false,
    showSplitInstalmentDown: showSplitInstalmentDown ?? false,
    showSecondCard,
    hidePaymentDetailsForInstalments,
    agreementChargeLabel,
    cardOneSectionLabel,
    scheduleModalOpen,
    handleOpenPaymentSchedule,
    handleClosePaymentSchedule,
  };
}
